import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";

export default function CategoryGroups({ category, onBack, groups = [], highlightGroup = null }) {
  const queryClient = useQueryClient();
  const userId = useSelector((state) => state.auth.id);
  const [groupLoadingStates, setGroupLoadingStates] = useState({});
  
  // Compute initial joined group IDs from participants array
  const initialJoinedGroupIds = useMemo(() => {
    if (!groups || !userId) return new Set();
    const joinedGroupIds = new Set();
    groups.forEach(group => {
      const isUserParticipant = group.participants?.some(participant => {
        const participantId = typeof participant === 'string' ? participant : (participant._id || participant);
        return String(participantId) === String(userId);
      });
      if (isUserParticipant) {
        joinedGroupIds.add(group._id);
      }
    });
    return joinedGroupIds;
  }, [groups, userId]);
  
  const [userJoinedGroupIds, setUserJoinedGroupIds] = useState(initialJoinedGroupIds);

  const getCategoryTitle = () => {
    const categoryTitles = { learning: "Learning Groups", industry: "Industry Groups", "my-groups": "My Groups" };
    return categoryTitles[category] || "Groups";
  };

  const handleBack = () => onBack();

  // Sync userJoinedGroupIds when initialJoinedGroupIds changes
  useEffect(() => {
    setUserJoinedGroupIds(initialJoinedGroupIds);
  }, [initialJoinedGroupIds]);

  const handleJoinGroup = async (group) => {
    setGroupLoadingStates(prev => ({ ...prev, [group._id]: true }));
    setUserJoinedGroupIds(prev => new Set(prev).add(group._id));
    
    try {
      const { joinGroup } = await import("../../services/groupchatService");
      await joinGroup(group._id, userId);

      const categoryQueryKey = category === "my-groups" ? ["myGroups", userId] : [`${category}Groups`, userId];
      queryClient.setQueryData(categoryQueryKey, existingGroups => existingGroups?.map(existingGroup => 
        existingGroup._id === group._id 
          ? { ...existingGroup, participants: [...existingGroup.participants, { _id: userId }] } 
          : existingGroup
      ));

      queryClient.setQueryData(["myGroups", userId], existingMyGroups => {
        if (existingMyGroups?.some(existingGroup => existingGroup._id === group._id)) return existingMyGroups;
        return existingMyGroups
          ? [...existingMyGroups, { ...group, participants: [...group.participants, { _id: userId }] }]
          : [{ ...group, participants: [{ _id: userId }] }];
      });

      queryClient.setQueryData(["allCategories"], (existingCategoriesData = { learningGroups: [], industryGroups: [] }) => ({
        learningGroups: existingCategoriesData.learningGroups.map(existingGroup => 
          existingGroup._id === group._id 
            ? { ...existingGroup, participants: [...existingGroup.participants, { _id: userId }] } 
            : existingGroup
        ),
        industryGroups: existingCategoriesData.industryGroups.map(existingGroup => 
          existingGroup._id === group._id 
            ? { ...existingGroup, participants: [...existingGroup.participants, { _id: userId }] } 
            : existingGroup
        )
      }));

    } catch (error) {
      console.error("Error joining group:", error);
      setUserJoinedGroupIds(prev => { 
        const updatedGroupIds = new Set(prev); 
        updatedGroupIds.delete(group._id); 
        return updatedGroupIds; 
      });
      toast.error("Failed to join group");
    } finally {
      setGroupLoadingStates(prev => ({ ...prev, [group._id]: false }));
    }
  };

  const GroupCard = ({ group, handleJoinGroup, userJoinedGroupIds, groupLoadingStates, userId, highlightGroup }) => {
    const participantCount = group.participants?.length ?? 0;
    
    // Check if user is a participant - handle both string IDs and objects
    const isUserParticipant = group.participants?.some(participant => {
      const participantId = typeof participant === 'string' ? participant : (participant._id || participant);
      return String(participantId) === String(userId);
    });
    
    const hasUserJoined = isUserParticipant || userJoinedGroupIds.has(group._id);
    const isGroupLoading = groupLoadingStates[group._id];
    const isGroupHighlighted = highlightGroup === group.name;
    const groupData = {
      gains: [
        "Access to cutting-edge research",
        "Networking opportunities", 
        "Career guidance",
        "Hands-on project collaborations"
      ],
      activities: [
        "Weekly tech talks",
        "Code reviews", 
        "Research paper discussions",
        "Hackathons",
        "Mentorship programs"
      ],
      activityRate: "92% active",
    };

    return (
      <div className={`rounded-lg shadow-sm border p-6 mb-3 ${
        isGroupHighlighted ? 'border-2 border-gray-700 shadow-xl' : 'bg-white border-gray-400'
      }`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-xl font-bold text-gray-900">
                  {group.name || "Unnamed Group"}
                </h3>
                {isGroupHighlighted && (
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                    Recommended
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                <span role="img" aria-label="members">👤</span>
                <span>{participantCount} members</span>
              </div>
            </div>
          </div>
          
          <button
            className={`px-6 py-2 rounded-full font-medium transition-colors ${
              hasUserJoined
                ? "bg-gray-200 text-gray-700 cursor-default"
                : isGroupHighlighted
                ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-lg"
                : "bg-[#E011A0] text-white hover:bg-pink-700"
            }`}
            disabled={hasUserJoined || isGroupLoading}
            onClick={() => handleJoinGroup(group)}
          >
            {isGroupLoading ? "Joining..." : hasUserJoined ? "Joined" : "Join Group"}
          </button>
        </div>

        <div className="mb-5">
          <div className="flex items-center gap-2 mb-3">
            <span role="img" aria-label="description">💬</span>
            <h4 className="font-semibold text-gray-900">Description</h4>
          </div>
          <p className="text-gray-600 text-xs leading-relaxed">
            {group.description || "No description available."}
          </p>
        </div>

        <div className="mb-5">
          <div className="flex items-center gap-2 mb-3">
            <span role="img" aria-label="gains">📈</span>
            <h4 className="font-semibold text-gray-900">What you will Gain</h4>
          </div>
          <div className="flex flex-wrap gap-2">
            {groupData.gains.map((gainItem, index) => (
              <span
                key={index}
                className="text-xs bg-blue-50 text-blue-700 px-3 py-1 rounded-full border border-blue-200"
              >
                {gainItem}
              </span>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-3">
            <span role="img" aria-label="activities">📅</span>
            <h4 className="font-semibold text-gray-900">Community Activities</h4>
          </div>
          <div className="flex flex-wrap gap-2">
            {groupData.activities.map((activityItem, index) => (
              <span
                key={index}
                className="text-xs bg-green-50 text-green-700 px-3 py-1 rounded-full border border-green-200"
              >
                {activityItem}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <span className="text-sm">←</span>
          <span className="text-sm" >Back</span>
        </button>
        <h1 className="text-lg font-bold text-gray-900">
          {getCategoryTitle()}
        </h1>
      </div>

      {groups.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">📭</div>
          <p className="text-gray-500 text-lg">No groups found for this category.</p>
          <p className="text-gray-400 text-sm mt-2">
            Check back later for new groups or try a different category.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map((group) => (
            <GroupCard 
              key={group._id} 
              group={group}
              handleJoinGroup={handleJoinGroup}
              userJoinedGroupIds={userJoinedGroupIds}
              groupLoadingStates={groupLoadingStates}
              userId={userId}
              highlightGroup={highlightGroup}
            />
          ))}
        </div>
      )}
    </div>
  );
} 